import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import analytics from '../../../shared/services/analytics/analytics';
import AlphasAddToListContainer from '../../../containers/AlphasAddToList/AlphasAddToList';
import { OpenAlphaDetailsInNewTab } from './OpenAlphaDetails';
import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import { filtersSelector } from '../../../reducers/SimulationReducer/FiltersReducer/FiltersSelector';
import { addModifiers, checkCheckingStatus } from '../../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';

import './EditorBottomBarRight.scss';
import { isTestPeriodControlShownSelector, isTestPeriodShownSelector } from '../../../reducers/EditorReducer/EditorSelector';
import { setAlphaTestPeriodShown } from '../../../actions/AlphasActions';

interface EditorBottomBarRightProps {
  path: string[];
  data: any;
  handleCheckSubmission: () => void;
  handleSubmitAlpha: () => void;
  currentActiveTab: number;
  isFetchingCheck: boolean;
  hasErrors?: boolean;
  enabledIntro: boolean;
}

interface EditorBottomBarRightStateProps {
  filters: any;
  isTestPeriodControlShown: boolean;
  isTestPeriodShown: boolean;
}

type EditorBottomBarRightGeneric = EditorBottomBarRightProps & EditorBottomBarRightStateProps & InjectedIntlProps;

const EditorBottomBarRight = ({
  data,
  handleCheckSubmission,
  handleSubmitAlpha,
  isFetchingCheck,
  filters,
  currentActiveTab,
  intl,
  hasErrors = false,
  enabledIntro,
  path,
  isTestPeriodControlShown,
  isTestPeriodShown,
}: EditorBottomBarRightGeneric) => {
  const dispatch = useDispatch();
  const handleTestPeriodChange = () => {
    analytics.trackCustomEvent(isTestPeriodShown ? 'sim_click_hide_test_period' : 'sim_click_show_test_period', { event_label: 'footer' });
    dispatch(setAlphaTestPeriodShown([...path, 'alphaDetail'], !isTestPeriodShown));
  };
  const disabledSubmission =
    !enabledIntro &&
    (data === null ||
      data === undefined ||
      isFetchingCheck ||
      hasErrors ||
      (isTestPeriodControlShown && !isTestPeriodShown) ||
      !data.getIn(['is', 'checks']) ||
      !checkCheckingStatus(data.getIn(['is', 'checks']).toJS()) ||
      data.get('status') !== 'UNSUBMITTED');

  const modifiers = filters.getIn([currentActiveTab, 'results']) ? ['has-results'] : [];

  return (
    <div className={addModifiers('editor-bottom-bar-right', modifiers)}>
      <div className="editor-top-bar-right__actions">
        <AlphasAddToListContainer
          disabled={!data}
          alphasData={data ? [data.get('id')] : undefined}
          btnClasses={addModifiers('editor-top-bar-right__action', ['add-list'])}
          btnTitleClass={addModifiers('editor-top-bar-right__action-icon', ['add-list'])}
          btnLabel={formatMessageHelper(intl, 'alphasAddToList.addAlphaToAList')}
          // Preserves BRAINTECH-5236 Simulate-page event: same name, no event_label,
          // fired on the modal-open button click. See BRAINTECH-5372 plan §"Shared
          // Component Disambiguation".
          onTrackOpen={() => analytics.trackCustomEvent('sim_click_add_to_list')}
        />
        <OpenAlphaDetailsInNewTab alphaId={data?.get('id')} />
      </div>
      <div className="editor-bottom-bar__bottom-buttons">
        {isTestPeriodControlShown && !isTestPeriodShown ? (
          <button className="button button--primary" onClick={handleTestPeriodChange}>
            {isTestPeriodShown ? (
              <FormattedMessageHelper id="alphaDetails.hideTestPeriod" />
            ) : (
              <FormattedMessageHelper id="alphaDetails.revealTestPeriod" />
            )}
          </button>
        ) : (
          <button
            className="editor-button editor-bottom-bar__bottom-button"
            onClick={() => {
              analytics.trackCustomEvent('sim_click_check_submission');
              handleCheckSubmission();
            }}
            disabled={disabledSubmission}
          >
            <span className="editor-button__text">
              <FormattedMessageHelper
                tagName="span"
                id="editorBottomBarRight.checkSubmission"
                defaultMessage={localeData.en['editorBottomBarRight.checkSubmission']}
              />
            </span>
          </button>
        )}

        <button
          onClick={() => {
            analytics.trackCustomEvent('sim_click_submit_alpha');
            handleSubmitAlpha();
          }}
          disabled={disabledSubmission}
          className="intro-step-10 editor-button editor-bottom-bar__bottom-button  editor-bottom-bar__bottom-button--green"
        >
          <span className="editor-button__text">
            <FormattedMessageHelper
              tagName="span"
              id="editorBottomBarRight.submitAlpha"
              defaultMessage={localeData.en['editorBottomBarRight.submitAlpha']}
            />
          </span>
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: StateType, props: EditorBottomBarRightProps): EditorBottomBarRightStateProps => ({
  filters: filtersSelector(state),
  isTestPeriodControlShown: isTestPeriodControlShownSelector(state, { path: props.path }),
  isTestPeriodShown: isTestPeriodShownSelector(state, { path: props.path }),
});

export default connect<EditorBottomBarRightStateProps, any, any, StateType>(mapStateToProps, null)(injectIntl(EditorBottomBarRight));
